import { useState } from "react";
import { cn } from "@/lib/utils";

interface AlluLogoProps {
  className?: string;
  variant?: "dark" | "light";
  /** Altura em px (fallback). Tailwind className via `className` tem prioridade quando passado. */
  height?: number;
}

/**
 * Logo Allu — usa o PNG oficial em /public/allu-logo.png (dark, pra fundo claro).
 * Fallback automático para wordmark CSS caso o arquivo não exista.
 *
 * Uso:
 *   <AlluLogo className="h-10 sm:h-14 w-auto" />     // controla tamanho via Tailwind
 *   <AlluLogo height={48} />                          // controla via prop fixa
 */
export const AlluLogo = ({ className, variant = "dark", height }: AlluLogoProps) => {
  const [imgError, setImgError] = useState(false);
  const src = variant === "dark" ? "/allu-logo.png" : "/allu-logo-light.png";

  if (!imgError) {
    return (
      <img
        src={src}
        alt="Allu"
        style={height ? { height: `${height}px`, width: "auto" } : undefined}
        className={cn("select-none", className)}
        onError={() => setImgError(true)}
      />
    );
  }

  // Fallback CSS (caso o PNG não esteja no public/)
  const textColor = variant === "dark" ? "text-[#161617]" : "text-[#FAFAFA]";
  const fallbackSize = height ?? 40;
  return (
    <div
      className={cn("inline-flex items-baseline gap-0.5 select-none", className)}
      style={{ fontSize: `${fallbackSize * 0.85}px` }}
    >
      <span
        className={cn("leading-none", textColor)}
        style={{
          fontFamily: "Inter, system-ui, sans-serif",
          fontWeight: 900,
          letterSpacing: "-0.04em",
        }}
      >
        allu
      </span>
      <span
        className="inline-block rounded-full"
        style={{
          width: "0.32em",
          height: "0.32em",
          background: "#4BD084",
          marginLeft: "0.04em",
          marginBottom: "0.05em",
        }}
        aria-hidden
      />
    </div>
  );
};
